import { useState, useEffect } from 'react';
import api, { API_HOST } from '../../utils/api';
import { useToast } from '../shared/Toast';
import AssetPreviewModal from '../shared/AssetPreviewModal';
import { HiOutlineDownload, HiOutlineTrash, HiOutlineEye } from 'react-icons/hi';

const ASSET_LABELS = {
  blog: 'Blog Post', newsletter: 'Newsletter', email_sequence: 'Email Sequence',
  one_pager: 'One-Pager', banner: 'Banner', service_page: 'Service Page', presentation: 'Presentation',
  marketing_strategy: 'Marketing Strategy',
};

export default function AssetHistory() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [previewAsset, setPreviewAsset] = useState(null);
  const { addToast } = useToast();

  useEffect(() => {
    api.get('/assets/history').then(({ data }) => {
      setAssets(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this failed generation?')) return;
    try {
      await api.delete(`/assets/${id}`);
      setAssets(prev => prev.filter(a => a.id !== id));
      addToast('Failed generation removed');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to delete', 'error');
    }
  };

  const filtered = filter === 'all' ? assets : assets.filter(a => a.assetType === filter);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Asset History</h1>
      <p className="text-gray-500 mb-6">View, preview, and download all your generated assets.</p>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === 'all' ? 'bg-v2-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All</button>
        {Object.entries(ASSET_LABELS).map(([type, label]) => (
          <button key={type} onClick={() => setFilter(type)} className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === type ? 'bg-v2-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{label}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
          No assets found. Generate your first asset to see it here!
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Topic</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Branding</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(asset => (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{ASSET_LABELS[asset.assetType] || asset.assetType}</td>
                  <td className="px-4 py-3 text-sm">{asset.topic?.title}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded text-xs ${asset.isWhitelabel ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
                      {asset.isWhitelabel ? 'White Label' : 'Co-branded'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(asset.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${
                      asset.status === 'completed' ? 'bg-green-100 text-green-800' :
                      asset.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {asset.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-center">
                      {asset.status === 'completed' && asset.filePath && (
                        <>
                          <button onClick={() => setPreviewAsset(asset)} className="text-gray-500 hover:text-v2-blue" title="Preview">
                            <HiOutlineEye className="w-5 h-5" />
                          </button>
                          <a href={`${API_HOST}/api/assets/${asset.id}/download`} className="text-v2-blue hover:text-blue-700" title="Download">
                            <HiOutlineDownload className="w-5 h-5" />
                          </a>
                        </>
                      )}
                      {asset.status === 'failed' && (
                        <button onClick={() => handleDelete(asset.id)} className="text-gray-400 hover:text-red-600" title="Remove failed generation">
                          <HiOutlineTrash className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview modal */}
      {previewAsset && (
        <AssetPreviewModal
          previewUrl={`${API_HOST}/api/assets/${previewAsset.id}/preview`}
          downloadUrl={`${API_HOST}/api/assets/${previewAsset.id}/download`}
          fileFormat={previewAsset.fileFormat}
          assetLabel={ASSET_LABELS[previewAsset.assetType] || previewAsset.assetType}
          assetType={previewAsset.assetType}
          assetId={previewAsset.id}
          qualityReport={previewAsset.qualityReport}
          onClose={() => setPreviewAsset(null)}
        />
      )}
    </div>
  );
}
